import { useState } from "react";
import { Typography, TablePagination, Stack, Button } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { ExcelExportMenuItem } from "./ExcelExportMenuItem";
import {
  GridToolbarContainer,
  GridCsvExportMenuItem,
  GridToolbarExportContainer,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { type User } from "@/types/localstorage";
import { format } from "date-fns";
import Tooltip from "@mui/material/Tooltip";
import type { GridApiCommunity } from "@mui/x-data-grid-pro/internals";
import type { CommercialReceptionBookViewPlan } from "../types";
import { getMaskUnlockButtonState } from "../utils/feedUtils";

interface IProps {
  loading: boolean;
  user: User | undefined;
  page: number;
  pageSize: number;
  allCount: number;
  rowsPerPageOptions: number[];
  changePage: (pageIndex: number) => void;
  changePageSize: (pageSize: number) => void;
  clearSelection: boolean;
  setClearSelection: React.Dispatch<React.SetStateAction<boolean>>;
  plan: CommercialReceptionBookViewPlan;
  maskedRowIds: Set<number>;
  onMaskUnlock: (ids: number[]) => void;
}

const CustomToolbar: React.FC<IProps> = (props) => {
  const {
    loading,
    user,
    page,
    pageSize,
    allCount,
    rowsPerPageOptions,
    changePage,
    changePageSize,
    clearSelection,
    setClearSelection,
    plan,
    maskedRowIds,
    onMaskUnlock,
  } = props;

  // エクスポートボタン上のTooltipの表示/非表示の制御
  const [exportTooltipOpen, setExportTooltipOpen] = useState(false);

  // ここからならDataGridのApiRefを取得できる、おそらくDataGridコンポーネントの子コンポーネントであれば取得できる
  const gridApiRef: React.MutableRefObject<GridApiCommunity> =
    useGridApiContext();

  // エクスポートボタンの有効/無効を決める
  const selectionCount = gridApiRef.current.state.selection.length;
  const disabledExportBtn =
    loading || selectionCount === 0 || selectionCount >= 1000;

  // 選択中のマスク済みIDを取得
  // GridRowId(string | number)のうちnumberのものだけに絞り込む（行ID型が変わっても静かに壊れないように、無検証キャストは避ける）
  const selectedMaskedIds =
    plan === "pay_per_view"
      ? gridApiRef.current.state.selection
          .filter((id): id is number => typeof id === "number")
          .filter((id) => maskedRowIds.has(id))
      : [];

  // 一括閲覧ボタンの有効/無効状態とツールチップ文言を算出する
  // （選択0件、読み込み中、またはバックエンドの上限を超えた選択の場合は無効化）
  const {
    disabled: disabledUnlockBtn,
    tooltipTitle: unlockDisabledTooltipTitle,
  } = getMaskUnlockButtonState(selectedMaskedIds.length, loading);

  const getFormattedDate = (): string => {
    return format(new Date(), "yyyyMMdd");
  };

  const getFileName = (): string => {
    return `${getFormattedDate()}_商業登記受付帳データ`;
  };

  // 選択状態クリア指示が送られている場合は選択状態をクリアし、Stateをfalseに戻す
  // 当初、下記コードをuseEffect()で囲んでいたが、clearSelectionがtrueのままfalseに戻らず
  // 次回の再レンダリング時、useEffect()の副作用処理がスキップされる問題があったため
  // 現在はuseEffect()を取り除いている
  if (clearSelection) {
    gridApiRef.current.setSelectionModel([]);
    setClearSelection(false);
  }

  return (
    <>
      <GridToolbarContainer
        sx={{ p: 0, display: "flex", justifyContent: "space-between" }}
      >
        <Stack direction={"row"} alignItems="center">
          {/* エクスポートボタン */}
          <Tooltip
            title={
              <>
                一覧をエクスポートするには検索結果を1,000件未満に絞り込み、その上で必要な行を選択してください。
              </>
            }
            open={exportTooltipOpen}
            placement="top"
            onMouseEnter={() => {
              if (disabledExportBtn) {
                setExportTooltipOpen(true);
              }
            }}
            onMouseLeave={() => {
              if (exportTooltipOpen) {
                setExportTooltipOpen(false);
              }
            }}
          >
            <Typography
              sx={{
                background: disabledExportBtn ? "rgba(0, 0, 0, 0.12)" : "#FFF",
                borderRadius: "4px",
                margin: "16px",
                border: disabledExportBtn ? "none" : "solid #1976d2 1px",
              }}
            >
              <GridToolbarExportContainer
                disabled={disabledExportBtn}
                sx={{
                  // https://mui.com/material-ui/react-button/#sizes と同様のpaddingを設定
                  padding: "6px 16px",

                  // アイコンとテキストのセンターラインをあわせる
                  "& > span": {
                    paddingBottom: "2px",
                    marginRight: "4px",
                  },
                }}
              >
                {/* NOTE: 商業登記は有料・無料でエクスポート機能に差分なし */}
                {user?.isFree ? (
                  <>
                    <GridCsvExportMenuItem
                      options={{
                        fileName: getFileName(),
                        utf8WithBom: true,
                        allColumns: true, // 全カラム出力
                        // ここでTrueにしていても"GridColDef<R,V,F>.disableExport = true"の方が優先されるみたい
                      }}
                    />
                    <ExcelExportMenuItem
                      options={{
                        fileName: getFileName(),
                        sheetName: "商業登記受付帳",
                        allColumns: true,
                      }}
                    />
                  </>
                ) : (
                  <>
                    <GridCsvExportMenuItem
                      options={{
                        fileName: getFileName(),
                        utf8WithBom: true,
                        allColumns: true,
                      }}
                    />
                    <ExcelExportMenuItem
                      options={{
                        fileName: getFileName(),
                        sheetName: "商業登記受付帳",
                        allColumns: true,
                      }}
                    />
                  </>
                )}
              </GridToolbarExportContainer>
            </Typography>
          </Tooltip>
          {plan === "pay_per_view" && (
            <Tooltip
              title={unlockDisabledTooltipTitle}
              disableHoverListener={!disabledUnlockBtn}
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={disabledUnlockBtn}
                  startIcon={<LockIcon />}
                  onClick={() => {
                    onMaskUnlock(selectedMaskedIds);
                  }}
                  sx={{ mx: 1 }}
                >
                  一括閲覧
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        {/* 上部のページネーション */}
        <TablePagination
          component={"div"}
          count={allCount}
          page={page}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(_, page) => {
            changePage(page);
          }}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            changePageSize(Number(event.target.value));
          }}
          labelRowsPerPage={"ページあたりの行数:"}
          labelDisplayedRows={({ from, to, count }) => {
            return `${count.toLocaleString()}件中 ${from.toLocaleString()}件 〜 ${to.toLocaleString()}件`;
          }}
        />
      </GridToolbarContainer>
    </>
  );
};

export { CustomToolbar };
